import { type RouteLocationNormalized, type RouteRecordRaw } from 'vue-router'

import { resourceTypes, isBulkEditableResource } from '../constants'
import { type ResourceTypes } from '../types'

const buildChildRoute = (resourceType: ResourceTypes): RouteRecordRaw => {
  // Coupons don't have bulk editing functionality but are unfortunately treated as menu entities
  const baseChildren = !isBulkEditableResource(resourceType)
    ? ([] as RouteRecordRaw[])
    : [
        {
          component: async () =>
            import('../components/bulk-editing/BulkEditingDialog.vue'),
          name: `brand.menu.${resourceType}.bulkEdit`,
          path: 'bulk-edit/:bulkEditJobId?',
          props: (route: RouteLocationNormalized) => ({
            bulkEditJobId: route.params.bulkEditJobId,
            resourceType,
          }),
        },
      ]
  return {
    children: baseChildren.concat([
      {
        component: async () => import('../views/MenuNewView.vue'),
        name: `brand.menu.${resourceType}.new`,
        path: 'new',
        props: () => ({
          resourceId: undefined,
          resourceType,
        }),
      },
      {
        component: async () => import('../views/MenuDetailView.vue'),
        name: `brand.menu.${resourceType}.detail`,
        path: ':resourceId',
        props: (route: RouteLocationNormalized) => ({
          resourceId: route.params.resourceId,
          resourceType,
        }),
      },
    ]),
    component: async () => import('../views/MenuMainView.vue'),
    name: `brand.menu.${resourceType}`,
    path: resourceType,
    props: () => ({
      resourceType,
    }),
  }
}

export const menuRouter: RouteRecordRaw[] = [
  {
    children: [
      ...resourceTypes
        .filter((resourceType) => resourceType !== 'coupon')
        .map((resourceType) => buildChildRoute(resourceType)),
      {
        component: async () => import('../views/CanvasMenuEditorView.vue'),
        meta: {
          // The canvas editor embeds the Owner Agent in its own pane, so the
          // global floating widget would be a redundant second entry point.
          hideOwnerAgentWidget: true,
        },
        name: 'brand.menu.canvas-editor',
        path: 'canvas-editor/:locationId?/:menuId',
        props: (route: RouteLocationNormalized) => ({
          locationId: route.params.locationId as string,
          menuId: route.params.menuId as string,
        }),
      },
      {
        component: async () => import('../views/MenuReviewView.vue'),
        name: 'brand.menu.review',
        path: 'review',
      },
    ],
    component: {
      template: '<router-view />',
    },
    meta: {
      authZ: ({ user }) => user.can('read', 'menu'),
      title: 'restaurant.menu',
    },
    name: 'brand.menu',
    path: 'menu',
    redirect: { name: 'brand.menu.menu' },
  },
  {
    ...buildChildRoute('coupon'),
    meta: {
      authZ: ({ user }) => user.can('read', 'coupon'),
      title: 'restaurant.coupons',
    },
    name: 'brand.coupon',
    path: 'coupon',
  },
]
